import { Close } from '@mui/icons-material';
import { GlobalStyles, IconButton, styled, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { closeSnackbar, MaterialDesignContent, SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import { globalStyle } from '../../config/theme/globalStyle';
import { palette, theme } from '../../config/theme/theme';
import { PageLoaderPlaceholder } from '../components/PageLoader';
import { ErrorBoundary } from '../controllers/ErrorBoundary';
import { setupNumberExtensions } from '../types/number-extensions';
import { NotificationDisplayer } from '../utils/notifications/NotificationDisplayer';

const MAX_NUMBER_OF_STACKED_TOAST_MESSAGES = 5;
export const MODAL_CONTAINER_ID = 'cc-modal-container';
export const EXPORT_CONTAINER_ID = 'cc-export-container';

/**
 * Override the default background and text color of snackbars
 * to ensure WCAG-compliant color contrast
 */
const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => {
  const { info, success, warning, error } = palette.snackbars;
  return {
    '&.notistack-MuiContent-info': { backgroundColor: info.background, color: info.color },
    '&.notistack-MuiContent-success': { backgroundColor: success.background, color: success.color },
    '&.notistack-MuiContent-warning': { backgroundColor: warning.background, color: warning.color },
    '&.notistack-MuiContent-error': { backgroundColor: error.background, color: error.color },
  };
});

const SNACKBAR_COMPONENTS = {
  success: StyledMaterialDesignContent,
  error: StyledMaterialDesignContent,
  info: StyledMaterialDesignContent,
  warning: StyledMaterialDesignContent,
};

export const withSharedProviders = (Component: any) => {
  setupNumberExtensions();

  const Wrapper = (props: any) => {
    return (
      <ErrorBoundary>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SnackbarProvider
            autoHideDuration={4000}
            Components={SNACKBAR_COMPONENTS}
            maxSnack={MAX_NUMBER_OF_STACKED_TOAST_MESSAGES}
            disableWindowBlurListener={true}
            action={(snackbarId) => (
              <IconButton onClick={() => closeSnackbar(snackbarId)} size="small" color="inherit">
                <Close />
              </IconButton>
            )}
          >
            <BrowserRouter future={{ v7_relativeSplatPath: false, v7_startTransition: false }}>
              <ThemeProvider theme={theme}>
                <GlobalStyles styles={globalStyle} />
                <Component {...props} />
                <div id={MODAL_CONTAINER_ID} />
                <div id={EXPORT_CONTAINER_ID} style={{ position: 'fixed', top: 0, left: '-10000px', zIndex: 10000 }} />
                <PageLoaderPlaceholder />
                <NotificationDisplayer />
              </ThemeProvider>
            </BrowserRouter>
          </SnackbarProvider>
        </LocalizationProvider>
      </ErrorBoundary>
    );
  };

  return Wrapper;
};
